import { useRequiredContext } from "@redotech/react-util/context";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import {
  ConversionPermissions,
  MarketingPermissions,
  OrderManagementPermissions,
} from "@redotech/redo-model/permissions";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import BarChart10FilledIcon from "@redotech/redo-web/icon-old/bar-chart-10-filled.svg";
import BarChart10Icon from "@redotech/redo-web/icon-old/bar-chart-10.svg";
import { memo } from "react";
import { useMatch } from "react-router-dom";
import { MarketingCloudStatusesContext } from "../../../../redo-suite/marketing-cloud";

export const AnalyticsItems = memo(function AnalyticsItems() {
  const routes = useRequiredContext(MerchantRoutesContext);

  const { orderTrackingEnabled, tnplEnabled, emailSmsEnabled } =
    useRequiredContext(MarketingCloudStatusesContext);

  const canViewMarketingAnalytics = useHasPermission(
    MarketingPermissions.VIEW_MARKETING_ANALYTICS,
  );
  const canAccessConversion = useHasPermission(
    ConversionPermissions.ACCESS_CONVERSION,
  );
  const canViewReports = useHasPermission(
    OrderManagementPermissions.VIEW_REPORTS,
  );
  const canViewOrderTrackingAnalytics = useHasPermission(
    MarketingPermissions.VIEW_ORDER_TRACKING_ANALYTICS,
  );

  const showOrderTracking =
    orderTrackingEnabled && canViewOrderTrackingAnalytics;
  const showTryFirst = tnplEnabled && canAccessConversion;
  const showMarketing = emailSmsEnabled && canViewMarketingAnalytics;

  const defaultRoutePriority = [
    showMarketing && routes.getAnalyticsSubRoute("marketing"),
    showTryFirst && routes.getAnalyticsSubRoute("try-first"),
    canViewReports && routes.getAnalyticsSubRoute("reports"),
    showOrderTracking && routes.getAnalyticsSubRoute("order-tracking"),
  ].find(Boolean);

  const defaultRoute = defaultRoutePriority || "";

  const canViewAnyAnalytics = !!defaultRoute;

  const shouldShowAnalytics = canViewAnyAnalytics;

  const analyticsRoute = routes.analyticsHref;
  const anyChildSelected = useMatch(`${analyticsRoute}/*`);

  if (!shouldShowAnalytics) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: defaultRoute }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={BarChart10FilledIcon}
      friendlyName="Analytics"
      hidden={!shouldShowAnalytics}
      id="analytics"
      UnfilledIcon={BarChart10Icon}
    >
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getAnalyticsSubRoute("marketing"),
        }}
        friendlyName="Marketing"
        hidden={!showMarketing}
        id="analytics-marketing"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getAnalyticsSubRoute("order-tracking"),
        }}
        friendlyName="Order tracking"
        hidden={!showOrderTracking}
        id="analytics-order-tracking"
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: "/support/dashboard?dashboard=sales" }}
        friendlyName="Support"
        id="analytics-support"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getAnalyticsSubRoute("try-first"),
        }}
        friendlyName="Try first"
        hidden={!showTryFirst}
        id="analytics-try-first"
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.getAnalyticsSubRoute("reports") }}
        friendlyName="Reports"
        hidden={!canViewReports}
        id="analytics-reports"
      />
    </RedoSecondaryNavItem>
  );
});
